import { useContext, useState, useEffect } from 'react';
import { useParams, RouterContext, goTo, Link } from 't-lib-ui/router';
import MainLayout from '../../components/MainLayout';
import AdminHeader from '../../components/admin/AdminHeader';
import { FlowForm, FlowFormLeaveContext, ButtonBar, useFlowFormGlobal, registerKeyHandler, unregisterKeyHandler, ImagePickerModal } from 't-lib-ui/structure';
import { AppState } from '../../context/general';
import { authenticatedRequest, getAdminMagicSecret, getCdnUrl } from '../../connector/auth';
import { RestAdminFeed, RestCatalogAdmin, RestCatalog, RestCatalogCreate, RestCatalogUpdate, ImageCollection } from '../../schema-copy/api-schema';
import { RssTypes } from '../../schema-copy/feed-schema';
import { nonNull } from 't-lib-ui/helpers';
import { TextButton, DropDownSelector, DropDownSelectorValue, TextArea, Input } from 't-lib-ui/widgets';
import { chevronBackIcon, chevronForwardIcon, saveIcon, addIcon, imageIcon } from '../../components/icons';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import { usePostAdminCatalog, usePutAdminCatalog, usePutAdminFeed, getCatalog, getCatalogCategories, generateFeedDescription, spellcheckText, useGetStuff, getFeedSummaries } from '../../connector/api';
import { FetchError } from 't-lib-ui/connector';
import AITextArea from '../../components/admin/AITextArea';
import FeedAutocompletePicker from '../../components/admin/FeedAutocompletePicker';
import FeedImageEditor from '../../components/admin/FeedImageEditor';
import { RestFeedSummary } from '../../schema-copy/api-schema';

const AdminEditCatalogPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const appState = useContext(AppState);
  const cdnUrl = getCdnUrl(appState.state.authData!);
  const routerCtx = useContext(RouterContext);
  const [feed, setFeed] = useState<RestAdminFeed | null>(null);
  const [catalog, setCatalog] = useState<RestCatalogAdmin | RestCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [categories, setCategories] = useState<string[]>([]);

  // Form fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [catalogDescription, setCatalogDescription] = useState('');
  const [active, setActive] = useState(true);
  const [promotion, setPromotion] = useState('100');
  const [keywords, setKeywords] = useState('');
  const [category, setCategory] = useState('');
  const [type, setType] = useState('');
  const [domain, setDomain] = useState('');
  const [paid, setPaid] = useState<boolean | null>(null);
  const [headerImage, setHeaderImage] = useState('');
  const [siteUrl, setSiteUrl] = useState('');
  const [savingSiteUrl, setSavingSiteUrl] = useState(false);
  const [children, setChildren] = useState<string[]>([]);
  const [parent, setParent] = useState<string | undefined>(undefined);
  const [parentSummary, setParentSummary] = useState<RestFeedSummary | undefined>(undefined);

  // Image picker state
  const [showImagePicker, setShowImagePicker] = useState(false);

  // Auto-generate tracking
  const [hasAutoGenerated, setHasAutoGenerated] = useState(false);
  const [lastModel, setLastModel] = useState('');

  // Initial values for change tracking
  const [initialValues, setInitialValues] = useState<string | null>(null);

  const magic = getAdminMagicSecret() || '';
  const postAdminCatalog = usePostAdminCatalog(magic);
  const putAdminCatalog = usePutAdminCatalog(id, magic);
  const putAdminFeed = usePutAdminFeed(id, magic);

  // Load image collection for header image picker
  const { data: imageCollection } = useGetStuff<ImageCollection>('image-collection', magic);
  const availableImages = imageCollection?.images || [];

  const catalogExists = !!catalog;

  // Check if form is valid for saving
  const isFormValid = title.trim().length > 0 &&
                      catalogDescription.trim().length > 0 &&
                      domain.trim().length > 0 &&
                      promotion.trim().length > 0;

  // Check if there are any changes from initial values
  const currentValues = JSON.stringify({
    title, description, catalogDescription, promotion, keywords,
    category, type, domain, paid, headerImage,
    children
  });
  const hasChanges = !initialValues || currentValues !== initialValues;

  useFlowFormGlobal();

  useEffect(() => {
    registerKeyHandler('adminEditCatalogPage', {
      'Escape': () => { goTo(routerCtx, `/a/admin/feed/${id}`); return true; },
    }, false, 0);

    return () => unregisterKeyHandler('adminEditCatalogPage');
  }, [routerCtx, id]);

  // Auto-generate description for new catalog entries
  useEffect(() => {
    const autoGenerate = async () => {
      // Only auto-generate if:
      // 1. Feed is loaded
      // 2. No existing catalog (catalogExists is false)
      // 3. Catalog description is empty
      // 4. Haven't auto-generated yet
      // 5. Loading is complete (to ensure we have accurate catalogExists value)
      if (!feed || catalogExists || catalogDescription.trim() || hasAutoGenerated || loading) {
        return;
      }

      try {
        setHasAutoGenerated(true);
        // The AI feed analysis already produced a one-sentence catalog description on the initial
        // fetch — use it and skip the extra generation call.
        if (feed.aiDescription?.trim()) {
          setCatalogDescription(feed.aiDescription.trim());
          return;
        }
        const result = await generateFeedDescription(
          nonNull(appState.state.authData),
          id,
          undefined
        );
        setCatalogDescription(result.description);
        setLastModel(result.model);
      } catch (e) {
        console.error('Failed to auto-generate description:', e);
        // Don't show error to user, just silently fail
      }
    };

    autoGenerate();
  }, [feed, catalogExists, catalogDescription, hasAutoGenerated, loading, appState.state.authData, id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(false);

        // Fetch feed
        const feedResult = await authenticatedRequest<RestAdminFeed>(
          nonNull(appState.state.authData),
          'GET',
          'api',
          `/api/admin/feed/${id}`,
          false,
          { magic }
        );
        setFeed(feedResult);
        setSiteUrl(feedResult.siteUrl || '');

        // Fetch categories
        const categoriesResult = await getCatalogCategories(nonNull(appState.state.authData));
        setCategories(categoriesResult.categories);

        // Try to fetch catalog entry
        let catalogResult: RestCatalogAdmin | RestCatalog | null = null;
        try {
          catalogResult = await getCatalog(nonNull(appState.state.authData), id);
          setCatalog(catalogResult);
        } catch (e) {
          if (e instanceof FetchError && e.status == 404) {
            // Catalog doesn't exist yet - this is fine
            setCatalog(null);
          } else {
            throw e;
          }
        }

        // Initialize form fields
        let initialTitle: string;
        let initialDescription: string;
        let initialCatalogDescription: string;
        let initialPromotion: string;
        let initialKeywords: string;
        let initialCategory: string;
        let initialType: string;
        let initialDomain: string;
        let initialPaid: boolean | null;
        let initialHeaderImage: string;
        let initialChildren: string[] = [];
        let initialParent: string | undefined = undefined;

        if (catalogResult) {
          // Existing catalog entry
          initialTitle = catalogResult.title || '';
          initialDescription = catalogResult.description || '';
          initialCatalogDescription = catalogResult.catalogDescription || '';
          initialPromotion = String(catalogResult.promotion || 100);
          initialKeywords = (catalogResult.keywords || []).join(', ');
          initialCategory = catalogResult.category || '';
          initialType = catalogResult.type || '';
          initialDomain = catalogResult.domain || '';
          initialPaid = catalogResult.paid ?? null;
          initialHeaderImage = catalogResult.headerImage || '';
          initialChildren = catalogResult.children ?? [];
          initialParent = catalogResult.parent;

          setTitle(initialTitle);
          setDescription(initialDescription);
          setCatalogDescription(initialCatalogDescription);
          setActive(true); // Active is not editable in update, always true
          setPromotion(initialPromotion);
          setKeywords(initialKeywords);
          setCategory(initialCategory);
          setType(initialType);
          setDomain(initialDomain);
          setPaid(initialPaid);
          setHeaderImage(initialHeaderImage);
          setChildren(initialChildren);
          setParent(initialParent);
        } else {
          // New catalog entry - populate from feed
          initialTitle = feedResult.shortTitle || '';
          // Fall back to the AI-generated one-sentence description when the feed has no description.
          initialDescription = feedResult.description || feedResult.aiDescription || '';
          initialCatalogDescription = '';
          initialPromotion = '100';
          initialKeywords = (feedResult.keywords || []).join(', ');
          initialCategory = feedResult.category || '';
          initialType = feedResult.type || '';
          initialPaid = feedResult.paid ?? null;
          initialHeaderImage = '';

          setTitle(initialTitle);
          setDescription(initialDescription);
          setCatalogDescription(initialCatalogDescription);
          setActive(true);
          setPromotion(initialPromotion);
          setKeywords(initialKeywords);
          setCategory(initialCategory);
          setType(initialType);
          setPaid(initialPaid);
          setHeaderImage(initialHeaderImage);

          // Extract domain from siteUrl
          try {
            const url = new URL(feedResult.siteUrl);
            const hostname = url.hostname;
            // Remove www. prefix but keep TLD (e.g., slashdot.org instead of just slashdot)
            const domainName = hostname.replace(/^www\./, '');
            setDomain(domainName);
            initialDomain = domainName;
          } catch (e) {
            setDomain('');
            initialDomain = '';
          }
        }

        // Store initial values for change detection
        setInitialValues(JSON.stringify({
          title: initialTitle,
          description: initialDescription,
          catalogDescription: initialCatalogDescription,
          promotion: initialPromotion,
          keywords: initialKeywords,
          category: initialCategory,
          type: initialType,
          domain: initialDomain,
          paid: initialPaid,
          headerImage: initialHeaderImage,
          children: initialChildren
        }));
      } catch (e) {
        console.error('Failed to load data:', e);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, appState.state.authData, magic]);

  // Resolve parent summary for display
  useEffect(() => {
    if (!parent || !appState.state.authData) {
      setParentSummary(undefined);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const results = await getFeedSummaries(nonNull(appState.state.authData), [parent]);
        if (!cancelled) setParentSummary(results[0]);
      } catch (e) {
        if (!cancelled) console.error('Failed to load parent summary:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [parent, appState.state.authData]);

  const handleBack = () => {
    // Check for 'from' query parameter to determine where to go back
    const from = new URLSearchParams(window.location.search).get('from');
    if (from === 'catalogs') {
      goTo(routerCtx, '/a/admin/catalogs');
    } else {
      goTo(routerCtx, `/a/admin/feed/${id}`);
    }
  };

  const handleSave = async (fieldOverrides?: Partial<{
    description: string;
    headerImage: string;
  }>) => {
    if (!feed || saving) return;

    try {
      setSaving(true);
      setSaveError(undefined);

      // Use overrides if provided, otherwise use current state
      const currentDescription = fieldOverrides?.description ?? description;
      const currentHeaderImage = fieldOverrides?.headerImage ?? headerImage;

      // Parse keywords from comma-separated string
      const keywordArray = keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);

      const promotionNum = parseInt(promotion, 10);
      if (isNaN(promotionNum) || promotionNum < 0) {
        setSaveError('Promotion must be a non-negative number');
        return;
      }

      if (catalogExists) {
        // Update existing catalog
        const update: RestCatalogUpdate = {
          title,
          description: currentDescription,
          catalogDescription,
          promotion: promotionNum,
          keywords: keywordArray.length > 0 ? keywordArray : undefined,
          category: category || undefined,
          type: type || undefined,
          domain,
          paid,
          headerImage: currentHeaderImage || undefined,
          children
        };

        await putAdminCatalog(update);
      } else {
        // Create new catalog
        const create: RestCatalogCreate = {
          feedId: id,
          title,
          description: currentDescription,
          catalogDescription,
          active,
          promotion: promotionNum,
          keywords: keywordArray.length > 0 ? keywordArray : undefined,
          category: category || undefined,
          type: type || undefined,
          domain,
          paid,
          headerImage: currentHeaderImage || undefined,
          children: children.length > 0 ? children : undefined
        };

        const result = await postAdminCatalog(create);
        setCatalog(result);

        // Auto-verify the feed when creating catalog entry
        if (feed && !feed.manuallyVerified) {
          await putAdminFeed({ manuallyVerified: true });
          setFeed({ ...feed, manuallyVerified: true });
        }
      }

      // Update initial values after successful save (using current values, including overrides)
      setInitialValues(JSON.stringify({
        title, description: currentDescription, catalogDescription, promotion, keywords,
        category, type, domain, paid, headerImage: currentHeaderImage,
        children
      }));
    } catch (e) {
      console.error('Failed to save catalog:', e);
      setSaveError(e instanceof Error ? e.message : 'Failed to save catalog entry');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    goTo(routerCtx, '/a/admin/feed/next');
  };

  // Create category options for dropdown
  const categoryOptions: DropDownSelectorValue[] = [
    { id: '', text: '(None)', searchTextLc: 'none' },
    ...categories.map(cat => ({
      id: cat,
      text: cat.charAt(0).toUpperCase() + cat.slice(1),
      searchTextLc: cat.toLowerCase()
    }))
  ];

  const handleCategoryChange = (value: DropDownSelectorValue) => {
    setCategory(value.id);
  };

  // Create type options for dropdown
  const typeOptions: DropDownSelectorValue[] = [
    { id: '', text: '(None)', searchTextLc: 'none' },
    ...RssTypes.map(t => ({
      id: t,
      text: t.charAt(0).toUpperCase() + t.slice(1),
      searchTextLc: t.toLowerCase()
    }))
  ];

  const handleTypeChange = (value: DropDownSelectorValue) => {
    setType(value.id);
  };

  // Create paid options for dropdown
  const paidOptions: DropDownSelectorValue[] = [
    { id: 'unknown', text: 'Unknown', searchTextLc: 'unknown' },
    { id: 'free', text: 'Free', searchTextLc: 'free' },
    { id: 'paid', text: 'Paid', searchTextLc: 'paid' }
  ];

  const handlePaidChange = (value: DropDownSelectorValue) => {
    if (value.id === 'paid') {
      setPaid(true);
    } else if (value.id === 'free') {
      setPaid(false);
    } else {
      setPaid(null);
    }
  };

  // Convert boolean | null to dropdown value
  const paidDropdownValue = paid === true ? 'paid' : paid === false ? 'free' : 'unknown';

  return (
    <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
    <MainLayout
      selectedFeed="admin-feeds"
      right={({ onExpand }) => (
        <FlowForm main={true} className='full-margin'>
          <style>{`
            .catalog-edit-container {
              max-width: 700px;
              margin: 0 auto;
              padding: 12px 24px;
            }
            .catalog-edit-container h2 {
              margin: 0 0 8px 0;
              color: var(--gray-200);
              font-size: 1.75rem;
              font-weight: 600;
            }
            .catalog-form-field {
              margin-bottom: 24px;
            }
            .catalog-form-label {
              display: block;
              font-weight: 600;
              margin-bottom: 8px;
              color: var(--gray-350);
              font-size: 0.95rem;
            }
            .catalog-form-note {
              background: var(--gray-975);
              border-left: 4px solid var(--gray-500);
              padding: 12px 16px;
              margin-top: 24px;
              color: var(--gray-350);
              font-size: 0.9em;
              border-radius: 4px;
            }
            .catalog-edit-container textarea {
              font-size: 0.95rem;
              width: 100%;
              line-height: 1.5;
              box-sizing: border-box;
            }
            .catalog-edit-container input[type="text"],
            .catalog-edit-container input[type="number"] {
              font-size: 0.95rem;
            }
            .catalog-dropdown-field .DropDownSelector {
              width: auto !important;
              min-width: 200px;
            }
            .catalog-loading,
            .catalog-error {
              max-width: 700px;
              margin: 0 auto;
              padding: 60px 24px;
              text-align: center;
            }
            .catalog-error {
              color: var(--error-color);
            }
            .catalog-loading {
              color: var(--gray-500);
            }
            .catalog-image-preview {
              width: 80px;
              height: 80px;
              border: 1px solid var(--gray-850);
              border-radius: 4px;
              overflow: hidden;
              margin: 0 auto;
              background: var(--surface-raised);
            }
            .catalog-image-preview img {
              width: 80px;
              height: 80px;
              object-fit: contain;
              display: block;
              border-radius: 4px;
            }
            .catalog-image-preview-placeholder {
              width: 80px;
              height: 80px;
              background: var(--gray-950);
              display: flex;
              align-items: center;
              justify-content: center;
              fontSize: 0.75rem;
              color: var(--gray-550);
              border-radius: 4px;
            }
          `}</style>
          {loading && (
            <div className="catalog-loading">
              Loading catalog data...
            </div>
          )}
          {error && (
            <div className="catalog-error">
              Error loading catalog data!
            </div>
          )}
          {feed && !loading && !error && (
            <div className="catalog-edit-container">
              <AdminHeader title={catalogExists ? 'Edit Catalog Entry' : 'Create Catalog Entry'} onExpand={onExpand} />

              {(feed.category == 'adult' || feed.downreadPrivateComment?.toLowerCase().includes('adult')) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', background: '#f8d7da', borderRadius: '6px', marginBottom: '16px', border: '1px solid #f5c6cb' }}>
                  <span style={{ fontSize: '1.2em' }}>{'⚠'}</span>
                  <span style={{ fontWeight: 600, color: '#721c24' }}>Adult Content</span>
                  <span style={{ color: '#856404', fontSize: '0.9rem' }}>— This feed has been flagged as containing adult content.</span>
                </div>
              )}

              <ButtonBar size="small">
                <TextButton
                  label="Feed"
                  icon={chevronBackIcon}
                  onClick={handleBack}
                  focussable="flow"
                />
                <TextButton
                  label={catalogExists ? 'Save' : 'Create'}
                  icon={catalogExists ? saveIcon : addIcon}
                  onClick={handleSave}
                  focussable="flow"
                  type="major"
                  busy={saving}
                  enabled={saveError ? 'error' : (isFormValid && hasChanges ? 'enabled' : 'disabled')}
                />
                <TextButton
                  label="Next"
                  icon={chevronForwardIcon}
                  onClick={handleNext}
                  focussable="flow"
                />
              </ButtonBar>

              {saveError && (
                <div className="catalog-form-note" style={{ background: '#fee', borderLeft: '4px solid var(--error-color)', color: 'var(--error-color)', marginTop: '16px' }}>
                  {saveError}
                </div>
              )}

              <div style={{ marginTop: '32px' }}>
                <div className="catalog-form-field">
                  <label className="catalog-form-label">
                    Title
                  </label>
                  <Input
                    value={title}
                    onInput={setTitle}
                    placeholder="Feed title"
                    layout="singleCol"
                  />
                </div>

                <div className="catalog-form-field">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <label className="catalog-form-label" style={{ margin: 0 }}>
                      Description
                    </label>
                    {description && (
                      <TextButton
                        label="Clear"
                        onClick={async () => {
                          setDescription('');
                          // Auto-save after clearing (only if catalog already exists)
                          if (catalogExists) {
                            await handleSave({ description: '' });
                          }
                        }}
                        focussable="sideway"
                        type="minor"
                        size="small"
                      />
                    )}
                  </div>
                  <TextArea
                    value={description}
                    onInput={setDescription}
                    rows={3}
                    layout="singleCol"
                    borderless={false}
                  />
                </div>

                <AITextArea
                  value={catalogDescription}
                  onInput={setCatalogDescription}
                  placeholder="Description for catalog display"
                  rows={3}
                  label="Catalog Description"
                  required={true}
                  onGenerate={async (instructions) => {
                    const result = await generateFeedDescription(
                      nonNull(appState.state.authData),
                      id,
                      instructions
                    );
                    setLastModel(result.model);
                    return result.description;
                  }}
                  onImprove={async (text, instructions) => {
                    return await spellcheckText(
                      nonNull(appState.state.authData),
                      text,
                      instructions
                    );
                  }}
                />
                {lastModel && <div style={{ fontSize: '0.7rem', color: 'var(--gray-600)', marginTop: '-16px', marginBottom: '16px' }}>{lastModel}</div>}

                <div className="catalog-form-field catalog-dropdown-field">
                  <label className="catalog-form-label">
                    Category
                  </label>
                  <DropDownSelector
                    selections={categoryOptions}
                    value={category}
                    onSelect={handleCategoryChange}
                    focussable="flow"
                  />
                </div>

                <div className="catalog-form-field catalog-dropdown-field">
                  <label className="catalog-form-label">
                    Type
                  </label>
                  <DropDownSelector
                    selections={typeOptions}
                    value={type}
                    onSelect={handleTypeChange}
                    focussable="flow"
                  />
                </div>

                <div className="catalog-form-field catalog-dropdown-field">
                  <label className="catalog-form-label">
                    Paid/Free
                  </label>
                  <DropDownSelector
                    selections={paidOptions}
                    value={paidDropdownValue}
                    onSelect={handlePaidChange}
                    focussable="flow"
                  />
                </div>

                <div className="catalog-form-field">
                  <label className="catalog-form-label">
                    Keywords (comma-separated)
                  </label>
                  <Input
                    value={keywords}
                    onInput={setKeywords}
                    placeholder="technology, news, business"
                    layout="singleCol"
                  />
                </div>

                <div className="catalog-form-field">
                  <label className="catalog-form-label">
                    Site URL
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <Input
                        value={siteUrl}
                        onInput={setSiteUrl}
                        placeholder="https://example.com/"
                        layout="singleCol"
                      />
                    </div>
                    {siteUrl != (feed?.siteUrl || '') && (
                      <TextButton
                        label="Save"
                        icon={saveIcon}
                        onClick={async () => {
                          if (!siteUrl.trim() || savingSiteUrl) return;
                          setSavingSiteUrl(true);
                          try {
                            await putAdminFeed({ siteUrl: siteUrl.trim() });
                            setFeed(f => f ? { ...f, siteUrl: siteUrl.trim() } : f);
                          } catch (e) {
                            console.error('Failed to update site URL:', e);
                          } finally {
                            setSavingSiteUrl(false);
                          }
                        }}
                        focussable="flow"
                        type="major"
                        size="small"
                        busy={savingSiteUrl}
                      />
                    )}
                  </div>
                </div>

                <div className="catalog-form-field">
                  <label className="catalog-form-label">
                    Domain
                  </label>
                  <Input
                    value={domain}
                    onInput={setDomain}
                    placeholder="example"
                    layout="singleCol"
                  />
                </div>

                <div className="catalog-form-field">
                  <label className="catalog-form-label">
                    Promotion Priority
                  </label>
                  <Input
                    value={promotion}
                    onInput={setPromotion}
                    placeholder="100"
                    layout="singleCol"
                  />
                </div>

                <div className="catalog-form-field">
                  <label className="catalog-form-label">
                    Feed Image
                  </label>
                  <FeedImageEditor feed={feed} magic={magic} allowCustomCreation={true} onUpdated={setFeed} />
                </div>

                {feed?.downreadPublicComment && (
                  <div className="catalog-form-field">
                    <label className="catalog-form-label">
                      Public Comment (Read-only)
                    </label>
                    <div style={{
                      padding: '12px',
                      background: 'var(--gray-975)',
                      border: '1px solid var(--gray-850)',
                      borderRadius: '4px',
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.75rem',
                      lineHeight: 1.5,
                      color: 'var(--gray-350)'
                    }}>
                      {feed.downreadPublicComment}
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '0.6rem' }}>
                      <Link to={`/a/admin/feed/${id}`} focussable="flow">Edit on Feed Page</Link>
                    </div>
                  </div>
                )}

                {feed?.downreadPrivateComment && (
                  <div className="catalog-form-field">
                    <label className="catalog-form-label">
                      Private Comment (Read-only)
                    </label>
                    <div style={{
                      padding: '12px',
                      background: 'var(--gray-975)',
                      border: '1px solid var(--gray-850)',
                      borderRadius: '4px',
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.75rem',
                      lineHeight: 1.5,
                      color: 'var(--gray-350)'
                    }}>
                      {feed.downreadPrivateComment}
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '0.6rem' }}>
                      <Link to={`/a/admin/feed/${id}`} focussable="flow">Edit on Feed Page</Link>
                    </div>
                  </div>
                )}

                <div className="catalog-form-field">
                  <label className="catalog-form-label">
                    Header Image
                  </label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
                    <TextButton
                      label="Select Image"
                      icon={imageIcon}
                      onClick={() => setShowImagePicker(true)}
                      focussable="flow"
                      type="minor"
                    />
                    {headerImage && (
                      <TextButton
                        label="Clear"
                        onClick={async () => {
                          setHeaderImage('');
                          // Auto-save after clearing (only if catalog already exists)
                          if (catalogExists) {
                            await handleSave({ headerImage: '' });
                          }
                        }}
                        focussable="sideway"
                        type="minor"
                        size="small"
                      />
                    )}
                  </div>
                  {headerImage && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{
                        width: '286px',
                        height: '143px',
                        border: '1px solid var(--gray-850)',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        margin: '0 auto',
                        background: 'var(--surface-raised)'
                      }}>
                        <img
                          src={(() => {
                            return `${cdnUrl}images/${headerImage}-w400.webp`;
                          })()}
                          srcSet={(() => {
                            return `${cdnUrl}images/${headerImage}-w400.webp 400w, ${cdnUrl}images/${headerImage}-w600.webp 600w`;
                          })()}
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 600px, 400px"
                          alt="Header preview"
                          style={{
                            width: '286px',
                            height: '143px',
                            objectFit: 'cover',
                            display: 'block'
                          }}
                          onError={(e) => {
                            // Fallback to .png if webp doesn't exist
                            (e.target as HTMLImageElement).src = `${cdnUrl}images/${headerImage}.png`;
                          }}
                        />
                      </div>
                      <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '0.85rem', color: 'var(--gray-400)' }}>
                        {headerImage}
                      </div>
                    </div>
                  )}
                </div>

                <div className="catalog-form-field">
                  <label className="catalog-form-label">
                    Member Feeds (Children)
                  </label>
                  <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginBottom: '8px' }}>
                    Group other feeds under this catalog entry. Each child must already have its own catalog entry. Adding a feed here automatically sets its parent reference; removing it clears the parent.
                  </div>
                  <FeedAutocompletePicker
                    value={children}
                    onChange={setChildren}
                    excludeId={id}
                    placeholder="Search feeds to add as members…"
                  />
                </div>

                {parent && (
                  <div className="catalog-form-field">
                    <label className="catalog-form-label">
                      Parent Catalog
                    </label>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 10px',
                      border: '1px solid var(--gray-850)',
                      borderRadius: '6px',
                      background: 'var(--gray-975)',
                      fontSize: '0.9rem'
                    }}>
                      {parentSummary?.iconUrl
                        ? <img src={parentSummary.iconUrl} alt="" width={16} height={16} />
                        : <span>📰</span>}
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {parentSummary?.shortName ?? parent}
                      </span>
                      <Link to={`/a/admin/catalog/${parent}`} focussable="flow">Edit Parent ↗</Link>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginTop: '6px' }}>
                      This is set automatically when an admin adds this feed to another catalog's children list.
                    </div>
                  </div>
                )}

                {!catalogExists && (
                  <div className="catalog-form-note">
                    Active status will be set to true when created.
                  </div>
                )}
              </div>
            </div>
          )}

          <ImagePickerModal
            show={showImagePicker}
            onClose={() => setShowImagePicker(false)}
            onSelect={async (imageName: string) => {
              setHeaderImage(imageName);
              setShowImagePicker(false);
              // Auto-save after selecting (only if catalog already exists)
              if (catalogExists) {
                await handleSave({ headerImage: imageName });
              }
            }}
            images={availableImages}
            title="Select Header Image"
          />
        </FlowForm>
      )}
      startWithLeftCollapsed={false}
    />
    </FlowFormLeaveContext.Provider>
  );
};

export default AdminEditCatalogPage;
